/**
 * Printing a document (or saving it as a PDF) with the browser: a hidden frame holding a copy
 * of the words as drawn (the gaps between sheets left out, page breaks as CSS breaks), on the
 * page's paper with its margins and page numbers. The frame runs no script (the app's CSP
 * forbids inline ones): the app calls its `print()`.
 */
import type { PageLayout } from "@protocol/PageLayout";
import { PAPER } from "../../../state/documents";

/** `text` safe inside HTML. */
function escape(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** The printed document's look (the editor's, without the screen's parts). */
function printCss(layout: PageLayout): string {
  const [w, h] = PAPER[layout.size] ?? PAPER.a4;
  const [width, height] = layout.orientation === "landscape" ? [h, w] : [w, h];
  const [top, right, bottom, left] = layout.margins;
  return `
@page { size: ${width}mm ${height}mm; margin: ${top}mm ${right}mm ${bottom}mm ${left}mm;
  @bottom-center { content: counter(page); font: 9pt Georgia, serif; color: #666; } }
html, body { margin: 0; background: #fff; color: #111; }
body { font: 11pt/1.5 Georgia, "Times New Roman", serif; }
h1.doc-title { font-size: 26pt; margin: 0 0 12pt; }
h2 { font-size: 18pt; margin: 14pt 0 6pt; } h3 { font-size: 14pt; margin: 12pt 0 4pt; } h4 { font-size: 12pt; margin: 10pt 0 4pt; }
p { margin: 0 0 8pt; } blockquote { margin: 0 0 8pt 18pt; padding-left: 10pt; border-left: 3px solid #bbb; color: #444; }
table { border-collapse: collapse; width: 100%; margin: 0 0 8pt; } td, th { border: 1px solid #999; padding: 3pt 5pt; vertical-align: top; }
figure.doc-image { margin: 0 0 8pt; break-inside: avoid; } figure.doc-image img { display: block; width: 100%; }
.doc-image-frame { position: relative; display: inline-block; max-width: 100%; line-height: 0; }
.doc-image-handle, .doc-image-layout, .doc-image-options { display: none !important; }
body { isolation: isolate; } .doc-page-break { clear: both; }
figcaption { font-size: 9pt; color: #555; }
.doc-page-break { break-after: page; height: 0; border: 0; }
.doc-mention { color: inherit; text-decoration: underline dotted; }
.doc-comment { background: none; }
[data-conflict] { display: none; }
.doc-gap, .ProseMirror-widget, .ProseMirror-gapcursor, .column-resize-handle { display: none !important; }
code { font-family: ui-monospace, monospace; font-size: 0.92em; }
`;
}

/** Prints the document drawn in `dom` on `layout`'s paper, titled `title`. */
export function printPage(dom: HTMLElement, layout: PageLayout, title: string): void {
  const copy = dom.cloneNode(true) as HTMLElement;
  copy.querySelectorAll(".doc-gap, .ProseMirror-widget, .ProseMirror-separator, .ProseMirror-trailingBreak").forEach((e) => e.remove());
  copy.removeAttribute("contenteditable");
  const frame = document.createElement("iframe");
  frame.id = "doc-print-frame";
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  frame.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><title>${escape(title)}</title><style>${printCss(layout)}</style></head><body>${copy.innerHTML}</body></html>`;
  frame.addEventListener("load", () => {
    const win = frame.contentWindow;
    if (!win) return frame.remove();
    win.addEventListener("afterprint", () => setTimeout(() => frame.remove(), 0));
    win.focus();
    win.print();
    // A browser that never says the printing ended: the frame goes after a minute anyway.
    setTimeout(() => frame.remove(), 60_000);
  });
  document.body.append(frame);
}
